import * as _ from 'lodash';
// eslint-disable-next-line no-restricted-imports
import moment from 'moment';
import { useCallback, useState } from 'react';
import type { ChangeEventHandler, VoidFunctionComponent } from 'react';

import { useDialogContext } from '../../../contexts/DialogContext';
import { GENERIC_AVATAR_IMG_SRC } from '../../../helpers/constants';
import Button from '../Button/Button';
import ButtonGroup from '../Button/ButtonGroup';
import IconButton from '../Button/IconButton';
import Flex from '../Flex';
import FromNow from '../FromNow/FromNow';
import { icons } from '../Icon';
import Tooltip from '../Tooltip';
import type { CommentUser } from '../../../redux/comments/actions';

type CommentMode = 'view' | 'edit';

type CommentItemProps = {
  id: number;
  user: CommentUser;
  createdAt: string;
  body: string;
  onUpdateComment: (commentId: number, updatedCommentBody: string) => void;
  onDeleteComment: (commentId: number) => void;
};

type CommentItemEditProps = Pick<CommentItemProps, 'id'> & {
  commentText: string;
  onCommentChange: ChangeEventHandler<HTMLTextAreaElement>;
  onUpdateComment: (id: number, commentText: string) => void;
  onCancelEdit: () => void;
};

type CommentItemViewProps = Pick<CommentItemProps, 'user' | 'createdAt'> & {
  commentText: string;
  onEdit: () => void;
  onRemove: () => void;
};

const CommentItemEdit: VoidFunctionComponent<CommentItemEditProps> = (
  props,
) => {
  return (
    <div className="comment-box">
      <textarea
        placeholder="Click here to leave a comment"
        value={props.commentText}
        onChange={props.onCommentChange}
      />
      <div className="comment-box-footer">
        <ButtonGroup>
          <Button type="secondary" size="medium" onClick={props.onCancelEdit}>
            Cancel
          </Button>
          <Button
            type="primary"
            size="medium"
            onClick={_.partial(
              props.onUpdateComment,
              props.id,
              props.commentText,
            )}
          >
            Submit
          </Button>
        </ButtonGroup>
      </div>
    </div>
  );
};

const CommentItemView: VoidFunctionComponent<CommentItemViewProps> = (
  props,
) => {
  return (
    <div className="comment-item-body">
      <Flex justify="space-between">
        <span>
          <b>{props.user.username}</b> commented{' '}
          <Tooltip
            id="comment-timestamp-tooltip"
            tooltip={moment(props.createdAt).format('MMMM Do, YYYY hh:mm')}
          >
            <span>
              <FromNow date={props.createdAt} />
            </span>
          </Tooltip>
        </span>
        <ButtonGroup spacing="xs">
          <IconButton icon={icons.edit} onClick={props.onEdit} />
          <IconButton icon={icons.remove} onClick={props.onRemove} />
        </ButtonGroup>
      </Flex>
      <p>{props.commentText}</p>
    </div>
  );
};

const CommentItem: VoidFunctionComponent<CommentItemProps> = ({
  id,
  user,
  createdAt,
  body,
  onUpdateComment,
  onDeleteComment,
}) => {
  const { onConfirm } = useDialogContext();
  const [mode, setMode] = useState<CommentMode>('view');
  const [commentText, setCommentText] = useState(body);

  const onRemoveClick = useCallback(() => {
    onConfirm({
      title: 'Delete Comment',
      body: 'Are you sure you want to delete this comment?',
      onConfirm: () => {
        return onDeleteComment(id);
      },
    });
  }, [id, onConfirm, onDeleteComment]);

  const cancelCommentEdit = useCallback(() => {
    setMode('view');
    setCommentText(body);
  }, [body]);

  const avatarUrl = user.github?.avatar_url || GENERIC_AVATAR_IMG_SRC;
  return (
    <div className="comment-item-container">
      <div className="comment-avatar">
        <img
          src={avatarUrl}
          width="100%"
          className="img-circle img-thumbnail"
          alt="user-avatar"
        />
      </div>
      {mode === 'view' ? (
        <CommentItemView
          user={user}
          commentText={commentText}
          createdAt={createdAt}
          onEdit={() => setMode('edit')}
          onRemove={onRemoveClick}
        />
      ) : (
        <CommentItemEdit
          id={id}
          commentText={commentText}
          onCancelEdit={cancelCommentEdit}
          onCommentChange={(e) => setCommentText(e.target.value)}
          onUpdateComment={onUpdateComment}
        />
      )}
    </div>
  );
};

export default CommentItem;
